/* eslint-disable react/no-unknown-property */
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ONE_CMS_API, OnVisible, SafeKeyLogo, BasicHeader,
} from '@americanexpress/gravity-core';
import Settings from './Settings';
import { TIMEOUT, SETTINGSDATA, SETTINGSCLICKED } from '../ducks';

const GravityHeader = (props) => {
  /* eslint complexity: ["error", 30] */
  const {
    hideSettings,
    flow: {
      statusCode,
      setting: {
        settingsOn,
        otcSmsPreference,
        otcEmailPreference,
        preferredLanguage,
        consentPreference,
        payWithPointsPreference, emiPreference,
        commChannelOn, emiOn, payWithPointOn,
        languageOn,
      },
      countryCode,
      currencyCode,
      amount,
      cardEndingDigits,
      timeouts,
    },
    gemUrl,
    updateSpinner,
    changeLang,
    updateSettingsPayloadByType,
    queryParams,
    currentScreenId,
    processPayAuthApi,
    logger,
    updateDataHeader,
    dataHeader,
  } = props;

  const [data, setData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [errorPage, setErrorPage] = useState(false);
  const [formattedCurrency, setFormattedCurrency] = useState(null);
  const [toggleSettings, setToggleSettings] = useState({
    otpVerification: consentPreference || null,
    textMessage: otcSmsPreference || null,
    email: otcEmailPreference || null,
    pointOptionValue: payWithPointsPreference || null,
    emiOptionValue: emiPreference || null,
  });

  useEffect(() => {
    updateSettingsPayloadByType(SETTINGSDATA, toggleSettings);
  }, [toggleSettings]);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    const currentLang = changeLang || preferredLanguage;
    const apiUrl = `${gemUrl}${ONE_CMS_API}/${currentLang}-${countryCode}/header.json`;

    updateSpinner(true);
    fetch(apiUrl, { signal })
      .then((res) => res.json())
      .then((headerData) => {
        setData(headerData);
      })
      .catch(() => {
        setErrorPage(true);
      });

    if (settingsOn && (!commChannelOn && !emiOn && !payWithPointOn && !languageOn)) {
      logger(
        {
          error: `settingsOn : ${settingsOn},commChannelOn : ${commChannelOn}, emiOn : ${emiOn}, payWithPointOn : ${payWithPointOn}, languageOn : ${languageOn} either of commChannelOn, emiOn, payWithPointOn, languageOn should return true when settingsOn is true or else we show a empty Settings Modal.`,
        },
        statusCode
      );
    }

    return () => {
      abortController.abort();
    };
  }, [changeLang]);

  useEffect(() => {
    if (typeof amount === 'number') {
      const formatAry = new Intl.NumberFormat(`${preferredLanguage}-${countryCode}`, {
        style: 'currency',
        currency: currencyCode,
      }).formatToParts(amount);
      let updateFormatted = '';
      formatAry.forEach((obj) => {
        if (obj.type !== 'literal' && obj.type !== 'currency') {
          updateFormatted += obj.value;
        }
      });
      setFormattedCurrency(updateFormatted);
    }
  }, [amount]);

  const onClickSettings = () => {
    updateSettingsPayloadByType(SETTINGSCLICKED, true);
    setShowModal(!showModal);
    setTimeout(() => {
      setShowModal(false);
      updateSettingsPayloadByType(TIMEOUT, true);
    }, timeouts.settings || timeouts.default);

    if (!showModal) {
      updateSettingsPayloadByType(TIMEOUT, false);
    } else {
      updateSettingsPayloadByType(TIMEOUT, true);
    }
  };

  const renderOverlay = () => (
    <Settings
      handleCloseOverlay={(userData, lang) => onClickSettings(userData, lang)}
      show={showModal}
      setToggleSettings={setToggleSettings}
      toggleSettings={toggleSettings}
      processPayAuthApi={processPayAuthApi}
      updateDataHeader={updateDataHeader}
      dataHeader={dataHeader}
      {...props}
      data={data}
    />
  );

  if (errorPage) {
    return <BasicHeader />;
  }

  return (
    <Fragment>
      {data && data.title && (
        <div id="safekeyHeader">
            {showModal && renderOverlay()}
            {currentScreenId && (
              <OnVisible
                identifier="axp-gravity-header"
                acsTransId={queryParams.acsTransId}
                screenId={currentScreenId}
              />
            )}
          <header className="safekey-header row">
            <div className="col-sm-5 col-xs-5 pad-0-l">
              <img
                className="header-logo"
                src={SafeKeyLogo}
                alt="American Express SafeKey"
              />
            </div>
            <div className="col-sm-7 col-xs-7 flex flex-column pad-1-r">
              <div className="flex flex-justify-end flex-align-center height-full">
                <div
                  className="legal-1 safekey-border-margin"
                >
                  <div className="text-align-right flex flex-justify-end flex-align-center">
                    <span
                      className="icon icon-sm dls-icon-card dls-gray-04 safekey-icon"
                      access="yes"
                      role="img"
                      aria-label={data.cardImg.aria}
                    />
                    <span className="safekey-sm">{cardEndingDigits}</span>
                  </div>
                  {currencyCode && formattedCurrency && (
                  <div className="text-align-right flex flex-justify-end flex-align-center">
                    <i
                      className="icon icon-sm dls-icon-merchandise dls-gray-04 safekey-icon"
                      role="img"
                      access="yes"
                      aria-label={data.tranAmt.aria}
                    />
                    <span className="safekey-sm">{`${currencyCode} ${formattedCurrency}`}</span>
                  </div>
                  )}
                </div>
                {settingsOn && hideSettings
                  && (commChannelOn || emiOn || payWithPointOn || languageOn) && (
                    <button
                      className="icon dls-icon-setting icon-sm dls-bright-blue safekey-settings-icon"
                      aria-label={data.settings.main.settingsIcon.aria}
                      id="settingsClickBtn"
                      type="button"
                      data-testid="settingsClickBtn"
                      onKeyPress={onClickSettings}
                      onClick={onClickSettings}
                    />
                )}
              </div>
            </div>
          </header>
        </div>
      )}
    </Fragment>
  );
};

GravityHeader.propTypes = {
  updateSpinner: PropTypes.func.isRequired,
  changeLang: PropTypes.string.isRequired,
  gemUrl: PropTypes.string.isRequired,
  updateSettingsPayloadByType: PropTypes.func.isRequired,
  loadedWithErrors: PropTypes.func.isRequired,
  hideSettings: PropTypes.bool.isRequired,
  flow: PropTypes.shape({
    statusCode: PropTypes.string,
    cardEndingDigits: PropTypes.string,
    formattedAmount: PropTypes.string,
    countryCode: PropTypes.string,
    currencyCode: PropTypes.string,
    amount: PropTypes.number,
    setting: PropTypes.shape({
      preferredLanguage: PropTypes.string,
      settingsOn: PropTypes.bool.isRequired,
      otcSmsPreference: PropTypes.string,
      otcEmailPreference: PropTypes.string,
      consentPreference: PropTypes.string,
      payWithPointsPreference: PropTypes.string,
      emiPreference: PropTypes.string,
      commChannelOn: PropTypes.string,
      emiOn: PropTypes.string,
      payWithPointOn: PropTypes.string,
      languageOn: PropTypes.string,
    }),
    timeouts: PropTypes.shape({
      settings: PropTypes.number,
      default: PropTypes.number,
    }),
  }),
  queryParams: PropTypes.shape({
    acsTransId: PropTypes.string.isRequired,
    mockEnabled: PropTypes.string.isRequired,
  }).isRequired,
  reportError: PropTypes.func.isRequired,
  currentScreenId: PropTypes.string.isRequired,
  processPayAuthApi: PropTypes.string.isRequired,
  logger: PropTypes.func.isRequired,
  updateDataHeader: PropTypes.func.isRequired,
  dataHeader: PropTypes.string.isRequired,
};

GravityHeader.defaultProps = {
  flow: {
    countryCode: 'US',
    setting: {
      preferredLanguage: 'en',
    },
    currencyCode: 'USD',
    amount: 0,
    cardEndingDigits: 0,
  },
};

export default GravityHeader;
